/**
 * Where an engagement model's snapshots sit, and what each one reads.
 *
 * A fit model trains on a window and validates on another, which `datasetTimeline`
 * draws as two bands. An engagement model is built differently: it takes a few
 * SNAPSHOT dates. At each one, a lead that converts in the next 30 days is a
 * conversion, read on the 90 days of activity before it converted; any other
 * lead is read on the 90 days before the snapshot. The training set is several
 * snapshots a month apart; validation is one more, later.
 *
 * The windows of neighbouring snapshots overlap by construction (90 days back,
 * a month apart), so on one line they were a single smear. Each snapshot gets
 * its own row: the activity it reads, its date, and the 30 days in which a
 * conversion counts.
 *
 * Both numbers live in the training templates springbok fetches from
 * mk-tenant-config (`analysis/behavioral_{pql,mqa}/training/default.yaml`:
 * `- 90` on event_day, `conversion_date < '{{training_date}}' + 30`), not in
 * this repo. Geometry only, like its sibling. The dates come in as the dataset
 * form stores them: comma separated `YYYY-MM-DD` for training, one for
 * validation.
 */

/** Days of activity read before each snapshot. */
export const LOOKBACK_DAYS = 90;

/** Days after a snapshot in which a conversion counts for it. */
export const CONVERSION_DAYS = 30;

export interface Snapshot {
  kind: 'training' | 'validation';
  /** The snapshot date, as given. */
  date: string;
  /** Percent of the axis from its left edge. */
  leftPct: number;
  /** The activity window before it, as a band ending at the tick. */
  windowLeftPct: number;
  windowWidthPct: number;
  /** The conversion window after it, a band starting at the tick. */
  conversionWidthPct: number;
  /** "Dec 11, 2025". */
  label: string;
}

export interface SnapshotTimeline {
  snapshots: Snapshot[];
  /** "3 snapshots, Dec 11, 2025 - Feb 11, 2026", or the one date. */
  trainingLabel: string;
  validationLabel: string;
  /** The axis ends, as labels. */
  from: string;
  to: string;
  /** The first of each month inside the axis, for faint guides across rows. */
  months: { label: string; leftPct: number }[];
  /** True when the validation snapshot is not after every training one, which
   *  is the one arrangement worth noticing: the same leads scored twice. */
  validationOverlaps: boolean;
}

// Stored dates are plain days, parsed as UTC midnight; formatted in the local
// zone a `2025-12-11` prints as Dec 10 anywhere west of Greenwich.
const DAY_LABEL = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
} as const;
const MONTH_LABEL = {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
} as const;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The dates in a stored list, in order, dropping anything unparseable. */
export function parseDates(list: string | null | undefined): number[] {
  if (!list) return [];
  return list
    .split(',')
    .map((each) => Date.parse(each.trim()))
    .filter((time) => !Number.isNaN(time))
    .sort((a, b) => a - b);
}

const day = (time: number) =>
  new Date(time).toLocaleDateString('en-US', DAY_LABEL);

export function buildSnapshotTimeline(
  trainingDates: string | null | undefined,
  evaluationDate: string | null | undefined,
  lookbackDays: number = LOOKBACK_DAYS,
  conversionDays: number = CONVERSION_DAYS
): SnapshotTimeline | null {
  const training = parseDates(trainingDates);
  const validation = parseDates(evaluationDate);
  if (!training.length || validation.length !== 1) return null;

  const lookback = lookbackDays * MS_PER_DAY;
  const outcome = conversionDays * MS_PER_DAY;
  const all = [...training, validation[0]];
  const axisFrom = Math.min(...all) - lookback;
  const axisTo = Math.max(...all) + outcome;
  const span = axisTo - axisFrom;
  if (span <= 0) return null;

  const place = (kind: Snapshot['kind'], time: number): Snapshot => ({
    kind,
    date: new Date(time).toISOString().slice(0, 10),
    leftPct: ((time - axisFrom) / span) * 100,
    windowLeftPct: ((time - lookback - axisFrom) / span) * 100,
    windowWidthPct: (lookback / span) * 100,
    conversionWidthPct: (outcome / span) * 100,
    label: day(time),
  });

  // Month starts strictly inside the axis, in UTC like the stored days.
  const months: SnapshotTimeline['months'] = [];
  const cursor = new Date(axisFrom);
  cursor.setUTCDate(1);
  cursor.setUTCHours(0, 0, 0, 0);
  cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  while (cursor.getTime() < axisTo) {
    months.push({
      label: cursor.toLocaleDateString('en-US', {
        month: 'short',
        timeZone: 'UTC',
      }),
      leftPct: ((cursor.getTime() - axisFrom) / span) * 100,
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  const first = training[0];
  const last = training[training.length - 1];
  const trainingLabel =
    training.length === 1
      ? day(first)
      : `${training.length} snapshots, ${day(first)} - ${day(last)}`;

  return {
    snapshots: [
      ...training.map((time) => place('training', time)),
      place('validation', validation[0]),
    ],
    trainingLabel,
    validationLabel: day(validation[0]),
    from: new Date(axisFrom).toLocaleDateString('en-US', MONTH_LABEL),
    to: new Date(axisTo).toLocaleDateString('en-US', MONTH_LABEL),
    months,
    validationOverlaps: validation[0] <= last,
  };
}
